import React, { useMemo, useState } from 'react';
import DataTable from '../DataTable';
import StatCard from '../StatCard';
import Badge from '../common/Badge';
import { useAppContext } from '../../context/AppContext';
import { formatCurrency, getStatusText } from '../../utils/helpers';
import { t } from '../../i18n';

const STATUS_TONE = {
  'pendiente_pm': 'warning',
  'pendiente_compras': 'warning',
  'aprobada': 'success',
  'enviada': 'info',
  'recibida': 'success'
};

const SuppliersOrdersBilling = () => {
  const { purchaseOrders, suppliers } = useAppContext();
  const [statusFilter, setStatusFilter] = useState('Todas');

  const rows = useMemo(() => {
    return purchaseOrders.map((order) => {
      const supplier = suppliers.find((item) => item.id === order.supplierId);
      return {
        id: order.id,
        supplierName: supplier?.name || order.supplierName || 'Proveedor',
        status: order.status,
        totalValue: Number(order.totalValue || 0),
        invoicedValue: Number(order.invoicedValue || 0),
        pendingValue: Number(order.totalValue || 0) - Number(order.invoicedValue || 0),
        createdDate: order.createdDate
      };
    });
  }, [purchaseOrders, suppliers]);

  const filteredRows = useMemo(() => {
    if (statusFilter === 'Todas') return rows;
    return rows.filter((row) => row.status === statusFilter);
  }, [rows, statusFilter]);

  const totals = useMemo(() => {
    return filteredRows.reduce(
      (acc, row) => {
        acc.total += row.totalValue;
        acc.invoiced += row.invoicedValue;
        acc.pending += row.pendingValue;
        return acc;
      },
      { total: 0, invoiced: 0, pending: 0 }
    );
  }, [filteredRows]);

  const columns = [
    { key: 'id', label: 'OC', sortable: true },
    { key: 'supplierName', label: t('Proveedor'), sortable: true },
    {
      key: 'status',
      label: t('Estado'),
      sortable: true,
      render: (value) => <Badge tone={STATUS_TONE[value] || 'neutral'}>{t(getStatusText(value))}</Badge>
    },
    { key: 'totalValue', label: t('Total'), sortable: true, align: 'right', render: (value) => formatCurrency(value) },
    { key: 'invoicedValue', label: t('Facturado'), sortable: true, align: 'right', render: (value) => formatCurrency(value) },
    { key: 'pendingValue', label: t('Pendiente'), sortable: true, align: 'right', render: (value) => formatCurrency(value) },
    { key: 'createdDate', label: t('Fecha'), sortable: true }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-neutral-900">{t('Ordenes y Facturacion')}</h1>
          <p className="text-sm text-neutral-500">{t('Resumen de ordenes, facturacion y trazabilidad contractual.')}</p>
        </div>
        <select
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          className="h-8 px-3 rounded border border-neutral-300 text-sm focus:outline-none focus:ring-1 focus:ring-brand-400 focus:border-brand-400"
        >
          <option>{t('Todas')}</option>
          <option>{t('pendiente_pm')}</option>
          <option>{t('pendiente_compras')}</option>
          <option>{t('aprobada')}</option>
          <option>{t('enviada')}</option>
          <option>{t('recibida')}</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard title={t('Monto total OC')} value={formatCurrency(totals.total)} />
        <StatCard title={t('Facturado')} value={formatCurrency(totals.invoiced)} />
        <StatCard title={t('Pendiente')} value={formatCurrency(totals.pending)} />
      </div>

      <DataTable
        data={filteredRows}
        columns={columns}
        searchPlaceholder={t('Buscar OC o proveedor...')}
      />
    </div>
  );
};

export default SuppliersOrdersBilling;
